/* eslint-disable max-lines */
import type { EntityDetailMode } from 'src/components/EntityDetail/mode';
import { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import { macroInfoMap } from '@redviking/argonaut-util/src/mes/macro-info-map';

const varType: {
    [t in Latest.AppletVariables.AppletVariables[string]['type']]: string;
} = {
    bool: 'Boolean',
    empty: 'Empty',
    num: 'Number',
    str: 'Text',
};

// TODO: validate where this is used and eventually remove it
const varStatus: {
    [s in 'error' | 'ok' | 'pending']: string;
} = {
    error: 'Error',
    ok: 'Ok',
    pending: 'Pending',
};

export const messages = {
    auth: {
        screen: {
            description: 'Require an Operator in order to interact with this screen',
            title: 'Require Operator Login',
        },
    },
    cells: {
        'attrLookup': {
            cond: {
                assigned: {
                    description: 'Checks if an attribute is assigned to a material',
                    text: 'Assignment',
                },
                nameMatch: {
                    description: 'Checks if the name of an attribute matches a search pattern',
                    text: 'Attribute Name Comparison',
                },
                propMatchNum: {
                    description: 'Compares the value of a numeric property to a constant',
                    text: 'Numeric Property Comparison (const)',
                },
                propMatchStr: {
                    description: 'Compares the value of a text property to a search pattern',
                    text: 'String Property Comparison',
                },
            },
            description: 'Display a table of attributes that satisfy conditions',
            error: {
                attrSchema: 'Attribute schema required',
                cols: 'No selected display columns',
                materialId: 'A variable for providing the material ID is required',
                missingVar: (varName: string) => `Cannot find referenced variable "${varName}" for condition operand`,
                propId: 'Comparisons against an attribute property requires a property to be selected',
                varNotSet: 'Variable selection is required for a condition with a variable source',
            },
            inline: {
                assignedToMaterial: 'That are assigned to the material with this ID',
                displayCols: 'Then, display these attribute properties as columns:',
                include: 'Include attributes from the schema:',
                satisfy: 'and satisfy all of the following conditions:',
                searchPattern: 'Separate multiple patterns with commas, use * for wildcards',
            },
            label: {
                constOpNum: 'Compare to value',
                constOpStr: 'Search pattern',
                var: 'Material ID variable',
            },
            prompt: {
                addCond: 'Add Condition',
                schema: 'Select an attribute schema',
            },
            title: 'Attribute Lookup Table',
        },
        'btn': {
            description: 'When pressed, a Button Cell can be configured to set a variable to a constant value, or to a timestamp of the moment the button was pressed, or to execute a script',
            labels: {
                // captureFocus: 'Automatically capture key strokes',
                color: 'Color',
                constantVal: 'Input a constant value',
                elevation: 'Button Elevation',
                enabled: 'Enabled',
                eventName: 'Applet Event Name',
                label: 'Label',
                size: 'Size',
                targetScreen: 'Select a target Screen',
                targetVar: 'Select a variable',
                varBehavior: 'What should this button do when pressed?',
            },
            title: 'Button',
        },
        'circularProgress': {
            condition: {
                boolOperation: 'The progress bar should flash red when {boolOperation} of the following comparisons are satisfied.',
            },
            description: 'Display a circular progress bar',
        },
        'datetime': {
            description: 'Display the current date and/or time',
            title: 'Current Date / Time',
        },
        'gauge': {
            description: 'Display a gauge that visualizes a numeric value',
            title: 'Gauge',
        },
        'input': {
            clearCondition: {
                boolOperation: 'When {boolOperation} of the following comparisons are satisfied, clear the input:',
                toggle: (current: boolean) => current
                    ? 'Remove Clear Condition'
                    : 'Add Clear Condition',
            },
            description: 'A textbox that can be typed in, and writes the value to a variable',
            hints: {
                btn: 'Submit button text',
                inputLabel: 'Label for the input',
            },
            labels: {
                btn: 'Submit button text',
                captureFocus: 'Enable auto-focus',
                enabledVar: 'Enabled',
                errorVar: 'Error Message',
                inputLabel: 'Label for the input',
                onlyIfEmpty: 'Only auto-focus if input is empty',
                trim: 'Trim whitespace around input',
                writeVar: 'Select a variable to write the input to',
            },
            title: 'Input',
        },
        'markdown': {
            description: 'Display formatted text using Markdown',
            title: 'Markdown',
        },
        'materialLookup': {
            description: 'Shows a table of material based on filter conditions',
            filters: {
                and: 'ALL',
                or: 'ANY',
                overview: 'Only include materials that satisfy {boolOperation} of the following filter conditions:',
                title: 'Material Filters',
            },
            outputVars: {
                description: 'Store the selected material data in these variables',
                materialId: 'Material ID',
                sn: 'Serial Number',
                title: 'Output Variables',

            },
            resetConditionalOverview: 'Refresh the list of material when {boolOperation} of the following conditions are met:',
            resetConditionalTitle: 'Refresh Condition',
            settingsTitle: 'Material Lookup Settings',
            title: macroInfoMap.materialLookup.title,
        },
        'media': {
            description: 'Display a carousel from uploaded or linked media',
            title: 'Media',
        },
        'process': {
            description: 'Cell Used to display process data',
            title: 'Process Cell',
        },
        'scanner-mqtt-publish': {
            description: 'Text entry that gets published to an mqtt topic',
            title: 'Scanner Mqtt Publish',
        },
        'select': {
            description: 'A dropdown that lets the user pick from a list of options and writes the selected value to a variable',
            labels: {
                addOption: 'Add Option',
                clearable: 'Allow clearing the selection',
                enabledVar: 'Enabled',
                label: 'Label',
                optionLabel: 'Option Label',
                optionValue: 'Option Value',
                options: 'Options',
                writeVar: 'Select a variable to write the selected value to',
            },
            title: 'Select',
        },
        'text': {
            description: 'Show text content',
            title: 'Text',
        },
        'timer': {
            description: 'Shows a value from a variable represented in a timer',
            title: 'Timer',
        },
        'workInstruction': {
            description: 'Display a work instruction',
            title: 'Display Work Instruction',
        },
        'workOrderModal': {
            description: 'Modal that conditionally allows for a new work order to be selected',
            showCondition: {
                boolOperation: 'When {boolOperation} of the following comparisons are satisfied, show the modal:',
            },
            title: 'Work Order Selection Modal',
            writeVar: 'Select a variable to store the selected work order id',
        },
    },
    comparisons: {
        addComparison: 'Add Comparison',
        comparison: {
            comparisonType: 'Comparison Type',
            comparisonTypes: {
                changed: 'Value Was Changed',
                eq: 'Equality',
                gt: 'Greater Than',
                isset: 'Is Set',
                lt: 'Less Than',
                regexp: 'RegExp Match',
                type: 'Variable Value Type',
            },
            not: 'NOT',
            operand: {
                num: 'Number',
                operand: 'Operand',
                str: 'Text',
                type: 'Operand Type',
                var: 'Variable',
            },
            varOp: 'Variable Operand',
        },
        evalTriggerEvent: 'When the specified applet event is emitted, the conditional assignments will be evaluated and the output variable will be set to the value of the first successful assignment',
        evalTriggerEventHeader: 'When an Applet Event is Triggered',
        evalTriggerOnChange: 'Whenever any of the inputs change, the conditional assignments will be evaluated and the output variable will be set to the value of the first successful assignment',
        evalTriggerOnChangeHeader: 'When a Variable in the following comparisons is Changed',
        validationErrs: {
            assignment: {
                inputVar: 'a variable name to provide the assignment value is required',
                invalid: (assignmentIndex: number) => `Assignment ${assignmentIndex + 1} is invalid`,
                invalidComparison: (comparisonIndex: number) => `Comparison ${comparisonIndex + 1} is invalid`,
                noComparisons: 'at least one comparison is required',
                varName: 'an output variable name is required',
                variableOperand: 'Variable operand cannot be left blank',
            },
            noAssignments: 'No assignments defined',
            triggerSources: 'If using specific inputs to trigger assignment evaluation, at least one input must be provided',
        },
    },
    defaultFont: 'Default Font',
    errors: {
        appConfig: {
            appletEventNotFound: (opts: { eventName: string, flowName: string }) => `Event with name "${opts.eventName}" does not exist. It is referenced in flow "${opts.flowName}"`,
            duplicateAppletEventName: (values: { eventName: string, cellName: string, cellTitle: string }) => `Event with name "${values.eventName}" on ${values.cellTitle} cell "${values.cellName || 'Unnamed'}" already exists`,
            duplicateVar: (values: { varName: string, providerName?: string, providerTitle: string }) => `Variable output "${values.varName}" in ${values.providerTitle} provider "${values.providerName || 'Unnamed'}" already exists`,
            invalidVarOrder: (values: { varName: string, providerName?: string, providerTitle: string }) => `Variable input "${values.varName}" in ${values.providerTitle} provider "${values.providerName || 'Unnamed'}" was used before it was defined.\n Check your variable providers and that they are in the appropriate order to reference their inputs.`,
            name: 'Config name is required',
            noAspectRatio: 'At least one screen size must be enabled',
            nonexistentVarCell: (values: { varId: string, cellType: string, cellLabel?: string }) => `Variable input "${values.varId}" referenced by ${values.cellType} cell "${values.cellLabel || 'Unnamed'}" does not exist.`,
            nonexistentVarVarp: (values: { varName: string, providerName?: string, providerTitle: string }) => `Variable input "${values.varName}" referenced by ${values.providerTitle} provider "${values.providerName || 'Unnamed'}" does not exist.`,
            scripts: {
                compileError: 'Script failed to compile',
                noId: 'A script must have an ID',
                notFound: 'A script cannot be empty',
                varNotFound: (varName: string) => `Applet variable "${varName}" referenced by script does not exist`,
            },
        },
    },
    hints: {
        AppletScriptEditor: {
            suggestions: {
                interpolates: [
                    {
                        name: 'ctrl',
                        type: 'code',
                        value: 'Ctrl',
                    },
                    {
                        name: 'space',
                        type: 'code',
                        value: 'space',
                    },
                ],
                message: 'Use {ctrl} + {space} to show suggestions',
            },
        },
        modifiers: 'Select variables to modify cell attributes. When the variable has a value, it will override whatever the attribute\'s default value is, as configured above.',
        varNameSelection: 'Ctrl+click to go to original provider',
        weight: 'Controls the ratio of space given to this cell, relative to its siblings',
    },
    labels: {
        addScreen: 'Add Screen',
        addVariable: 'Add Variable',
        appDevDevices: 'Active Devices',
        bannerHeader: 'Banner header',
        bgColor: 'Background Color',
        configLocation: 'Config Location',
        elevated: 'Elevated',
        linkCells: 'Linked Argonaut Cell',
        lockout: 'Lockout Variable',
        margins: 'Margins',
        modifiers: 'Modifiers',
        padding: 'Padding',
        screenName: 'Screen Name',
    },
    macros: {
        process: {
            collectTrigger: {
                label: 'Process data collection will be triggered when...',
                triggers: {
                    btn: 'a button is pressed',
                    conditional: {
                        collectConditional: {
                            boolOperation: 'When {boolOperation} of the following comparisons are satisfied, do a data collect:',
                        },
                        label: 'the following conditions are met',
                    },
                    event: 'an applet event is emitted',
                    sparkplug: 'a sparkplug metric is changed',
                    var: 'a variable is changed',
                },
            },
            defaultSparkplugNode: 'Use the default sparkplug node for the process',
            sparkplugTrigger: {
                device: 'Sparkplug Device',
                metric: 'Metric',
            },
            varTriggerLabel: 'Variable to trigger data collection',
        },
    },
    migration: {
        apply: (mode: EntityDetailMode) => mode === 'fork' ? 'Fork and Apply Changes' : 'Apply Changes',
        applyAll: (mode: EntityDetailMode) => mode === 'fork' ? 'Fork and accept all changes' : 'Accept all changes and continue',
        bannerWarning: 'This config was created with a previous version of Argonaut. Viewing an older config might cause unexpected behavior.',
        description1: 'This configuration was created with a previous version of Argonaut and needs to be migrated to the latest version.',
        description2: 'Some things might not work as expected until the migrations are applied.',
        description3: 'Please review the following changes before continuing:',
        noReview: 'None of the migrations require your review.',
        skip: 'Skip Migrations and Proceed Anyway',
        title: 'Heads Up!',
        unknownType: 'Auto-apply migration',
    },
    options: {
        advancedSzControls: 'Sizing Controls (Advanced)',
        simpleSzControls: 'Sizing Controls (Simple)',
    },
    screenDesigner: {
        grid: {
            screenPreview: {
                noCells: {
                    subtitle: 'Use the grid modifier tool ({toolIcon}) to add a cell',
                    title: 'This screen has no cells',
                },
            },
            subtitle: 'Create/ modify the layout of cells for an applet screen',
            title: 'Cell Layout',
        },
        subtitle: 'Configure the design of an applet screen as well as assign Argonaut cell types to cells on the screen',
        title: 'Screen Maintenance',
        toolbarBtns: {
            default: 'Default',
            wireframe: 'Wireframe',
        },
    },
    sparkplugNodeDevice: {
        desc: 'Select which variables to expose as Sparkplug metrics',
        grid: {
            headers: {
                name: 'Variable Name',
                permissions: 'Allow Writes',
                track: 'Track Value?',
            },
            toolTips: {
                permissions: {
                    descs: [
                        {
                            interpolates: [
                                {
                                    name: 'customVarp',
                                    type: 'code',
                                    value: 'Custom',
                                },
                                {
                                    name: 'dcmd',
                                    type: 'code',
                                    value: 'DCMD',
                                },
                            ],
                            message: 'Only variables defined in a {customVarp} variable provider are allowed to be written to from a Sparkplug device command ({dcmd})',
                        },
                    ],
                    title: 'Allow Writes',
                },
                title: 'Help',
                track: {
                    descs: [
                        {
                            interpolates: [
                                {
                                    name: 'myVar',
                                    type: 'code',
                                    value: '#My+Variable+Name',
                                },
                                {
                                    name: 'formattedVar',
                                    type: 'code',
                                    value: '-My-Variable-Name',
                                },
                            ],
                            message: 'If a variable is tracked then a Sparkplug metric will be created mirroring the name of the variable it is tracking (variable names will be formatted to sparklug metric name standards. i.e. {myVar} -> {formattedVar})',
                        },
                        {
                            interpolates: [],
                            message: 'If a variable is not tracked then no Sparkplug metric will be created for it, and it will not be available to other Sparkplug devices',
                        },
                    ],
                    title: 'Track Variable',
                },
            },
        },
        noVars: 'This Design Does Not Have Any Defined Variables',
        title: 'Sparkplug Metrics',
    },
    titles: {
        baseCellAttrs: 'Base Cell Attributes',
        configEdit: 'Editing Config',
        general: 'General',
        overview: 'Overview',
        providers: 'Providers',
        screens: 'Screens',
    },
    tooltips: {
        forkConfig: (configName: string) => `Fork "${configName}"`,
        hideInspector: 'Hide',
        showInspector: 'Show',
    },
    varName: 'Variable Name',
    varStatus,
    varType,
    warnings: {
        AppletScriptEditor: {
            main: 'WARNING: Scripts are powerful. Please carefully validate intended script purpose and implementation to minimize impact on overall system performance. Script API is likely to be extended and may change in the future. Current safeguard limitation on scripts ensures that script environements are limited to:',
            safeGaurds: [
                '8 MB of memory',
                '5 ms of execution time',
            ],
        },
    },
};
